'use client'

import { FC } from 'react'
import { SectionArticlesLandingPageProps } from '@portfolio/models'
import { styled } from '@portfolio/styles'
import {
  Chip,
  Container,
  Link,
  Media,
  SectionWrapper,
  TagsGroup,
} from '@portfolio/components'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/router'
import { ROUTE_BASE_ARTICLE, ROUTE_CATEGORY_ARTICLE } from '@portfolio/configs'

const StyledSectionWrapper = styled(SectionWrapper, {
  marginBottom: '$mobileSectionMarginL',
  '@lg': {
    marginBottom: '$desktopSectionMarginL',
  },
})

const Title = styled('h1', {
  projectFont: 'heading01',
  textAlign: 'center',
  marginBottom: '92px',
  paddingInline: '$20',
})

const ArticlesGrid = styled('div', {
  margin: '0 auto',
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '$56',
  maxWidth: '100%',

  '@md': {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '$64 $24',
  },
})

const Article = styled('div', {
  position: 'relative',
  width: '100%',
})

const ArticleTitle = styled('h3', {
  projectFont: 'heading07',
  gridRow: 2,
  gridColumn: 1,
})

const ArticleGridContent = styled('div', {
  position: 'relative',
  display: 'grid',
  rowGap: '$24',
  width: '100%',
  paddingTop: '$20',

  '@md': {
    rowGap: '$32',
  },
})

const MediaWrap = styled('div', {
  gridColumn: 1,
  gridRow: 1,
  borderRadius: '$r2',
  overflow: 'hidden',
  transition: 'transform .5s ease',

  '& img': {
    transition: 'transform .5s ease',
  },

  '@md': {
    '&:hover': {
      '& img': {
        transform: 'scale(1.04)',
      },
    },
  },
})

const ArticleTags = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gridColumn: 1,
  gridRow: 1,
  columnGap: '$12',
})

const ArticleTag = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '$10',
})

const StyledLink = styled(Link, {
  zIndex: '$content',
  projectFont: 'heading08',
  textDecoration: 'underline',
  gridColumn: 1,
  gridRow: 3,
  textUnderlineOffset: '.35em',
})

export const SectionArticlesLandingPage: FC<
  SectionArticlesLandingPageProps
> = ({ title, articles, tags, priority }) => {
  const { t } = useTranslation('articles')
  const { asPath } = useRouter()
  const pathname = asPath?.split('?')[0] || ''
  const isCategoryPage = new RegExp(
    `^${ROUTE_CATEGORY_ARTICLE.replace('/', '\\/')}/[^/]+$`,
  ).test(pathname)

  return (
    <StyledSectionWrapper type="FIRST_SECTION">
      {title && <Title>{title}</Title>}
      {tags && (
        <TagsGroup
          tags={tags.map((tag) => ({
            title: tag.title,
            value: tag.path,
            selected: tag.path === pathname,
            href: tag.path,
          }))}
          additionalTag={
            isCategoryPage
              ? { title: 'Clear', href: ROUTE_BASE_ARTICLE }
              : undefined
          }
        />
      )}
      <Container>
        <ArticlesGrid>
          {articles &&
            articles.map((article, index) => (
              <Article key={article.path}>
                <a
                  href={article.path}
                  tabIndex={-1}
                  aria-label={`Article - ${article.title}`}
                >
                  {article.thumbnail && (
                    <MediaWrap>
                      <Media
                        {...article.thumbnail}
                        hardcropRatio={'landscape3'}
                        priority={priority && index < 3}
                        sizes="(min-width: 740px) 50vw, 100vw"
                      />
                    </MediaWrap>
                  )}
                </a>
                <ArticleGridContent>
                  {article.tags && (
                    <ArticleTags>
                      <ArticleTag>
                        {article.tags.map((tag) => (
                          <Chip
                            key={tag.path}
                            title={tag.title}
                            type="dark"
                            href={tag.path}
                          />
                        ))}
                      </ArticleTag>
                    </ArticleTags>
                  )}
                  {article.title && (
                    <ArticleTitle>{article.title}</ArticleTitle>
                  )}
                  {article.path && (
                    <StyledLink href={article.path} appearance="text">
                      {t('readArticle')}
                    </StyledLink>
                  )}
                </ArticleGridContent>
              </Article>
            ))}
        </ArticlesGrid>
      </Container>
    </StyledSectionWrapper>
  )
}
